import React, { useEffect } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import { closeProductCardModal, ProductCardModal } from "web/redux/ducks/send-to-app-modal";
import { ReduxStoreState } from "web/redux/store";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import { RawBranchLink } from "web/script/utils/branch-io";
import history from "web/script/utils/history";
import storage from "web/script/utils/storage";
import url from "web/script/utils/url";

type FeedType = "general" | "is_pdp_related";

interface ProductCardSendToAppModalProps {
    feedType: FeedType;
}

function extractState(state: ReduxStoreState): ProductCardModal {
    return state.sendToAppModalReducer.productCard;
}

const APP_MODAL_NAME = "product-card-block-overlay";
const ANALYTICS_EVENT_LABEL = "product_card_block";
// IS PDP 'Similar Products' related section
const ANALYTICS_EVENT_LABEL_IS_RELATED = "product_card_block_is_related";
// IS PDP 'Recently OOS' related section
const ANALYTICS_EVENT_LABEL_OOS_RELATED = "product_card_block_oos_related";

export const TITLE_KEYS = {
    title_1: gettextNoop("product_card.app_overlay_block.title_1"),
    title_2: gettextNoop("product_card.app_overlay_block.title_2"),
    title_3: gettextNoop("product_card.app_overlay_block.title_3"),
    title_4: gettextNoop("product_card.app_overlay_block.title_4"),
    title_5: gettextNoop("product_card.oos_product_app_overlay_block.title_1"),
};

export default function ProductCardSendToAppModal({
    feedType,
}: ProductCardSendToAppModalProps): JSX.Element {
    const dispatch = useDispatch();
    const {
        openModal,
        productUrl,
        productImageUrl,
        inStock,
        retailerExcludedFromApp,
        appDeeplinkPath,
        linkId,
    } = useReduxSelector(extractState);

    const keepModalOpen = !!useOverlayWatcher(APP_MODAL_NAME);
    useEffect(() => {
        if (!keepModalOpen) {
            dispatch(closeProductCardModal());
            history.popOverlay(APP_MODAL_NAME);
        }
    }, [keepModalOpen, dispatch]);

    useEffect(() => {
        if (openModal) {
            // I check the current overlay state instead of modalIsOpen
            // to get around the delay introduced by hooks
            if (history.getCurrentState().overlay !== APP_MODAL_NAME) {
                history.pushOverlay(APP_MODAL_NAME);
            }
        }
    }, [openModal]);

    const appDeeplinkUrl = url.parse(appDeeplinkPath);
    const productLinkUrl = url.parse(productUrl);
    if (linkId) {
        appDeeplinkUrl.searchParams.set("link_id", linkId);
        productLinkUrl.searchParams.set("link_id", linkId);
    }
    let blockVariant;
    let branchLink;
    let title = "";
    let continueOption;
    let analyticsEventLabel;

    function getContinueOption(): JSX.Element {
        return (
            <ContinueToOption
                href={productLinkUrl.toString()}
                continueMessage={gettext("general.continue_shopping_btn_text")}
                analyticsEventLabel={analyticsEventLabel}
                continueClick={() => {
                    storage.set("continueClicked", true, true);
                }}
                closeModal={() => dispatch(closeProductCardModal())}
            />
        );
    }

    if (feedType === "is_pdp_related") {
        analyticsEventLabel = inStock
            ? ANALYTICS_EVENT_LABEL_IS_RELATED
            : ANALYTICS_EVENT_LABEL_OOS_RELATED;
        branchLink = RawBranchLink.ProductCardISPDPRelatedFeed;
        title = gettext("is_pdp_related.product_card.app_overlay_block.title_1");
    } else {
        const oosHardBlockExperiment =
            !inStock && environment.getFeature("mg_oos_product_card_block");
        analyticsEventLabel = ANALYTICS_EVENT_LABEL;
        title = oosHardBlockExperiment ? gettext(TITLE_KEYS.title_5) : gettext(TITLE_KEYS.title_1);
        branchLink = RawBranchLink.ProductCardGeneralFeed;
        blockVariant =
            oosHardBlockExperiment ||
            (retailerExcludedFromApp && environment.pageTypeIs(["feed", "search"]))
                ? "hard_block"
                : "soft_block";
        if (blockVariant === "soft_block") {
            continueOption = getContinueOption();
        }
    }

    function onClose(): void {
        dispatch(closeProductCardModal());

        // if we've given the user the option to continue then don't go to the product on close
        if (blockVariant === "soft_block" && !continueOption) {
            globals.window.location.href = appDeeplinkUrl.href;
        }
    }

    return (
        <SendToAppModal
            isOpen={openModal}
            onClose={onClose}
            title={title}
            url={branchLink}
            analyticsEventLabel={analyticsEventLabel}
            appDeepLinkURL={appDeeplinkUrl}
            productImageURL={productImageUrl}
            extraContent={(): JSX.Element => {
                return continueOption;
            }}
        />
    );
}
